import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaYoutube,
  FaLinkedin,
  FaTiktok,
  FaTelegram,
  FaGlobe,
  FaSyncAlt,
  FaExternalLinkAlt,
  FaUsers,
  FaChartLine,
  FaSatelliteDish,
  FaArrowUp,
  FaArrowDown,
} from 'react-icons/fa';

import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechButtonGhost,
  StatusPill,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import { listSocialProfiles } from '../../services/socialService';
import CronCountdown from '../common/CronCountdown';

const PLATFORM_META = {
  instagram: { label: 'Instagram', Icon: FaInstagram, color: '#e1306c' },
  facebook:  { label: 'Facebook',  Icon: FaFacebook,  color: '#1877f2' },
  twitter:   { label: 'X (Twitter)', Icon: FaTwitter, color: '#1da1f2' },
  x:         { label: 'X (Twitter)', Icon: FaTwitter, color: '#1da1f2' },
  youtube:   { label: 'YouTube',   Icon: FaYoutube,  color: '#ff0000' },
  linkedin:  { label: 'LinkedIn',  Icon: FaLinkedin, color: '#0a66c2' },
  tiktok:    { label: 'TikTok',    Icon: FaTiktok,   color: '#000000' },
  telegram:  { label: 'Telegram',  Icon: FaTelegram, color: '#26a5e4' },
};

const KIND_TO_PLATFORM = {
  // Heurística: por URL del outlet inferimos la plataforma.
  // Si no matchea ninguna, mostramos icono genérico.
};

function detectPlatform(url = '') {
  const u = String(url).toLowerCase();
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('facebook.com'))  return 'facebook';
  if (u.includes('youtube.com'))   return 'youtube';
  if (u.includes('twitter.com') || u.includes('x.com')) return 'twitter';
  if (u.includes('linkedin.com'))  return 'linkedin';
  if (u.includes('tiktok.com'))    return 'tiktok';
  if (u.includes('t.me') || u.includes('telegram'))     return 'telegram';
  return null;
}

function platformMeta(p) {
  return PLATFORM_META[p] || { label: p || 'Otro', Icon: FaGlobe, color: '#888' };
}

function fmtNum(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const x = Number(n);
  if (x >= 1_000_000) return (x / 1_000_000).toFixed(1) + 'M';
  if (x >= 1_000) return (x / 1_000).toFixed(1) + 'k';
  return String(x);
}

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  const days = h / 24;
  if (days < 7) return `hace ${Math.round(days)}d`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

/* ---------- styled ---------- */

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.75rem;
`;

const ProfileCard = styled.article`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.8rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 140px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${(p) => p.$accent || accent};
    opacity: 0.7;
  }

  header {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px solid ${borderSoft};

    .icon {
      width: 30px;
      height: 30px;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.04);
      color: ${(p) => p.$accent || accent};
      font-size: 0.95rem;
    }

    .name {
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;
      flex: 1;
      h4 {
        margin: 0;
        font-size: 0.92rem;
        color: var(--text-primary);
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .handle {
        font-family: ${mono};
        font-size: 0.65rem;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--text-disabled);
      }
    }

    a.ext {
      color: var(--text-disabled);
      font-size: 0.78rem;
      &:hover { color: ${accent}; }
    }
  }

  .stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }

  .stat {
    .lbl {
      font-family: ${mono};
      font-size: 0.55rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--text-disabled);
    }
    .val {
      font-size: 1.1rem;
      color: var(--text-primary);
      font-weight: 600;
      display: inline-flex;
      align-items: baseline;
      gap: 6px;
    }
    .delta {
      font-family: ${mono};
      font-size: 0.65rem;
      letter-spacing: 0.05em;
      &.up { color: var(--alert-success, #38d39f); }
      &.dn { color: var(--alert-error, #ff5470); }
    }
  }

  footer {
    margin-top: auto;
    display: flex;
    justify-content: space-between;
    font-family: ${mono};
    font-size: 0.55rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    padding-top: 0.35rem;
    border-top: 1px solid ${borderSoft};
  }

  .badge-own {
    background: rgba(0, 180, 216, 0.12);
    color: ${accent};
    border: 1px solid rgba(0, 180, 216, 0.3);
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 0.55rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }

  .error {
    color: var(--alert-error, #ff5470);
    font-size: 0.75rem;
    font-style: italic;
  }
`;

const OutletGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
`;

const OutletCard = styled.article`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.75rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 160px;

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px solid ${borderSoft};

    .left {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
    }

    .pico {
      width: 26px;
      height: 26px;
      border-radius: 5px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.04);
      color: ${(p) => p.$accent || accent};
      font-size: 0.85rem;
      flex-shrink: 0;
    }

    h4 {
      margin: 0;
      font-size: 0.9rem;
      color: var(--text-primary);
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    a.ext {
      color: var(--text-disabled);
      font-size: 0.7rem;
      &:hover { color: ${accent}; }
    }
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    flex: 1;
  }

  li {
    font-size: 0.78rem;
    line-height: 1.35;
    a {
      color: var(--text-secondary);
      text-decoration: none;
      &:hover { color: var(--text-primary); }
    }
    .when {
      display: block;
      font-family: ${mono};
      font-size: 0.58rem;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--text-disabled);
      margin-top: 1px;
    }
  }

  footer {
    display: flex;
    justify-content: space-between;
    font-family: ${mono};
    font-size: 0.55rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    padding-top: 0.35rem;
    border-top: 1px solid ${borderSoft};
  }

  .empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-disabled);
    font-size: 0.78rem;
    font-style: italic;
    text-align: center;
  }
`;

/* ---------- componente ---------- */

const SocialPresence = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loadState, setLoadState] = useState({ loading: true, error: null });

  const openProfile = (profile) => navigate(`/social/${profile.id}`);

  const load = () => {
    setLoadState({ loading: true, error: null });
    return listSocialProfiles()
      .then((profs) => {
        setProfiles(profs || []);
        setLoadState({ loading: false, error: null });
      })
      .catch((e) => setLoadState({ loading: false, error: friendlyError(e, 'No se pudieron cargar las redes.') }));
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    const platforms = new Set();
    let followers = 0;
    let withData = 0;
    let errored = 0;
    for (const p of profiles) {
      platforms.add(p.platform);
      const f = p.latest?.followers;
      if (typeof f === 'number') { followers += f; withData++; }
      if (p.latest?.status === 'error') errored++;
    }
    return {
      profiles_count: profiles.length,
      platforms_count: platforms.size,
      followers_total: followers,
      followers_known: withData,
      errored_count: errored,
    };
  }, [profiles]);

  if (loadState.loading) {
    return (
      <TechShell>
        <TechPageHeader
          title="Redes Sociales"
          subtitle="Cuentas monitoreadas: perfiles propios, competidores y medios"
          dossier="SOC://STREAM/01"
        />
        <Loading>Cargando feed real</Loading>
      </TechShell>
    );
  }

  if (loadState.error) {
    return (
      <TechShell>
        <TechPageHeader title="Redes Sociales" dossier="SOC://STREAM/01" />
        <ErrorBox>No se pudo cargar · {loadState.error}</ErrorBox>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="Redes Sociales"
        subtitle="Cuentas monitoreadas en Instagram, Facebook, X, YouTube, LinkedIn y más · monitoreo diario"
        dossier="SOC://STREAM/01"
        actions={
          <TechButtonGhost type="button" onClick={() => window.location.reload()}>
            <FaSyncAlt /> Refrescar
          </TechButtonGhost>
        }
      >
        <StatusPill>Perfiles <strong>{stats.profiles_count}</strong></StatusPill>
        <StatusPill>Plataformas <strong>{stats.platforms_count}</strong></StatusPill>
        <CronCountdown cronKey="social" label="Próximo scrape en" />
      </TechPageHeader>

      <StatStrip min="180px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaUsers /> Perfiles</StatLabel>
          <StatValue>{stats.profiles_count}</StatValue>
          <StatSub>monitoreados</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaSatelliteDish /> Plataformas</StatLabel>
          <StatValue>{stats.platforms_count}</StatValue>
          <StatSub>únicas</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaChartLine /> Seguidores</StatLabel>
          <StatValue>{fmtNum(stats.followers_total)}</StatValue>
          <StatSub>{stats.followers_known}/{stats.profiles_count} con dato</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaGlobe /> Con error</StatLabel>
          <StatValue style={{ color: stats.errored_count > 0 ? 'var(--alert-error)' : undefined }}>
            {stats.errored_count}
          </StatValue>
          <StatSub>{stats.errored_count === 0 ? 'todo OK' : 'último snapshot falló'}</StatSub>
        </StatTile>
      </StatStrip>

      <TechSection title={`Perfiles monitoreados · ${profiles.length}`}>
        {profiles.length === 0 ? (
          <Empty>
            Sin perfiles cargados todavía. Agregalos desde <strong>Configuración → Redes</strong>.
          </Empty>
        ) : (
          <ProfileGrid>
            {profiles.map((p) => {
              const meta = platformMeta(p.platform);
              const Icon = meta.Icon;
              const lat = p.latest || {};
              const prev = p.previous || {};
              const delta =
                typeof lat.followers === 'number' && typeof prev.followers === 'number'
                  ? lat.followers - prev.followers
                  : null;
              return (
                <ProfileCard
                  key={p.id}
                  $accent={meta.color}
                  onClick={() => openProfile(p)}
                  style={{ cursor: 'pointer' }}
                  title="Ver posts y comentarios"
                >
                  <header>
                    <span className="icon"><Icon /></span>
                    <div className="name">
                      <h4>{p.display_name || p.handle}</h4>
                      <span className="handle">@{p.handle} · {meta.label}</span>
                    </div>
                    {p.is_own && <span className="badge-own">propio</span>}
                    <a
                      className="ext"
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={p.url}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaExternalLinkAlt />
                    </a>
                  </header>
                  {lat.status === 'error' || (!lat.followers && lat.error_message) ? (
                    <div className="error">
                      {lat.error_message || 'Sin datos en la última medición'}
                    </div>
                  ) : (
                    <div className="stats">
                      <div className="stat">
                        <div className="lbl">Seguidores</div>
                        <div className="val">
                          {fmtNum(lat.followers)}
                          {delta != null && delta !== 0 && (
                            <span className={`delta ${delta > 0 ? 'up' : 'dn'}`}>
                              {delta > 0 ? <FaArrowUp /> : <FaArrowDown />} {fmtNum(Math.abs(delta))}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="stat">
                        <div className="lbl">Posts</div>
                        <div className="val">{fmtNum(lat.posts)}</div>
                      </div>
                    </div>
                  )}
                  <footer>
                    <span>{p.posts_count > 0 ? `${p.posts_count} posts` : (lat.status || '—')}</span>
                    <span>snap: {fmtRel(lat.snapshot_at)}</span>
                  </footer>
                </ProfileCard>
              );
            })}
          </ProfileGrid>
        )}
      </TechSection>


    </TechShell>
  );
};

export default SocialPresence;
